const HST_OFFSET_MINUTES = 10 * 60;
const MINUTE_IN_MS = 60 * 1000;
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_LOCAL_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const HAS_TIME_ZONE_SUFFIX = /([zZ]|[+-]\d{2}:?\d{2})$/;

type InputValue = string | Date | null | undefined;

type ParseOptions = {
  includeTime?: boolean;
};

const clamp = (value: number) => (Number.isFinite(value) ? value : 0);

const createNaiveDate = (year: number, month: number, day: number, hours = 0, minutes = 0): Date => {
  return new Date(year, month, day, hours, minutes, 0, 0);
};

const parseDateOnlyString = (value: string): Date | null => {
  if (!DATE_ONLY_REGEX.test(value.trim())) {
    return null;
  }
  const [year, month, day] = value.split('-').map((part) => clamp(Number(part)));
  if (!year || !month || !day) {
    return null;
  }
  return createNaiveDate(year, month - 1, day);
};

const parseDateTimeLocalString = (value: string): Date | null => {
  if (!DATE_TIME_LOCAL_REGEX.test(value.trim())) {
    return null;
  }
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) {
    return null;
  }
  const [year, month, day] = datePart.split('-').map((part) => clamp(Number(part)));
  const [hours, minutes] = timePart.split(':').map((part) => clamp(Number(part)));
  if (!year || !month || !day) {
    return null;
  }
  return createNaiveDate(year, month - 1, day, hours || 0, minutes || 0);
};

const convertInstantToNaiveHst = (date: Date, includeTime: boolean): Date => {
  const hstTimestamp = date.getTime() - HST_OFFSET_MINUTES * MINUTE_IN_MS;
  const hstDate = new Date(hstTimestamp);
  const year = hstDate.getUTCFullYear();
  const month = hstDate.getUTCMonth();
  const day = hstDate.getUTCDate();
  const hour = includeTime ? hstDate.getUTCHours() : 0;
  const minute = includeTime ? hstDate.getUTCMinutes() : 0;
  return createNaiveDate(year, month, day, hour, minute);
};

const convertNaiveHstToInstant = (date: Date, includeTime: boolean): Date => {
  const utcTimestamp = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    includeTime ? date.getHours() : 0,
    includeTime ? date.getMinutes() : 0,
    0,
    0,
  );
  const shifted = utcTimestamp + HST_OFFSET_MINUTES * MINUTE_IN_MS;
  return new Date(shifted);
};

const parseValue = (value: InputValue, { includeTime = false }: ParseOptions): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : convertInstantToNaiveHst(value, includeTime);
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (!includeTime && DATE_ONLY_REGEX.test(trimmed)) {
    return parseDateOnlyString(trimmed);
  }

  if (includeTime && DATE_TIME_LOCAL_REGEX.test(trimmed) && !HAS_TIME_ZONE_SUFFIX.test(trimmed)) {
    return parseDateTimeLocalString(trimmed);
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return convertInstantToNaiveHst(parsed, includeTime);
};

const formatDateOnly = (date: Date): string => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseHstDateValue = (value: InputValue): Date | null => parseValue(value, { includeTime: false });

export const parseHstDateTimeValue = (value: InputValue): Date | null => parseValue(value, { includeTime: true });

export const formatHstDateInput = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  return formatDateOnly(date);
};

export const formatHstDateTimeInput = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  const instant = convertNaiveHstToInstant(date, true);
  return instant.toISOString();
};

export const toHstDisplayDate = (date: Date | null | undefined): Date | null => {
  if (!date) return null;
  return convertNaiveHstToInstant(date, false);
};

export const toHstDisplayDateTime = (date: Date | null | undefined): Date | null => {
  if (!date) return null;
  return convertNaiveHstToInstant(date, true);
};

export const hasTimeZone = (value: string): boolean => HAS_TIME_ZONE_SUFFIX.test(value.trim());

export const isDateOnlyString = (value: string): boolean => DATE_ONLY_REGEX.test(value.trim());

export const isDateTimeLocalString = (value: string): boolean => DATE_TIME_LOCAL_REGEX.test(value.trim()) && !hasTimeZone(value);

export const HST_TIME_OFFSET_MINUTES = HST_OFFSET_MINUTES;
